import { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Range } from "react-date-range";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { tokens } from "@/tokens";
import { DateRangeFilter, SelectClientBanner } from "@/components";
import { useGetDashboardUpdateDateQuery, useGetPrefOriginOpportunitiesQuery } from "@/services";
import { useAppSelector } from "@/store";
import { useEffectiveClientScope } from "@/hooks";
import { formatDateOrTime, getLastYearToDateRange, responsiveFontSizes } from "@/utils";

import KpiCards from "./components/kpi-cards";
import OpportunitiesTable from "./components/opportunities-table";
import GroupedByManufacturer from "./components/grouped-by-manufacturer";
import EntriesDetail from "./components/entries-detail";

const PAGE_SIZE = 10;
const DEBOUNCE_MS = 400;

type ViewMode = "all" | "by-manufacturer";

const PreferentialOrigin: FC = (): ReactElement => {
  const { areClientsLoading } = useAppSelector(
    (state) => state.client
  );
  const { clientId, tenantId, isTDAdminTCD, shouldRequireClientSelection } =
    useEffectiveClientScope();

  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>("all");

  // Filters
  const [dateFilter, setDateFilter] = useState<Pick<Range, "startDate" | "endDate">>(
    () => getLastYearToDateRange()
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  // Detail view state
  const [selectedOpportunity, setSelectedOpportunity] = useState<PrefOriginOpportunity | null>(null);
  const [initialMid, setInitialMid] = useState<string | undefined>(undefined);

  // Debounce search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const dateFrom = useMemo(
    () => format(dateFilter.startDate ?? new Date(), "yyyy-MM-dd"),
    [dateFilter.startDate]
  );
  const dateTo = useMemo(
    () => format(dateFilter.endDate ?? new Date(), "yyyy-MM-dd"),
    [dateFilter.endDate]
  );

  const input: PrefOriginInput = useMemo(() => ({
    dateFrom,
    dateTo,
    clientId,
    ...(isTDAdminTCD && tenantId && { tenantId }),
    // In grouped view, fetch all opportunities so we can group client-side
    pageNo: viewMode === "by-manufacturer" ? 1 : page,
    pageSize: viewMode === "by-manufacturer" ? 500 : pageSize,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(statusFilter && { statusFilter }),
  }), [dateFrom, dateTo, clientId, isTDAdminTCD, tenantId, page, pageSize, debouncedSearch, statusFilter, viewMode]);

  const commonOptions = useMemo(
    () => ({
      skip: shouldRequireClientSelection,
      refetchOnMountOrArgChange: true,
    }),
    [shouldRequireClientSelection]
  );

  // Queries
  const { data: lastUpdated } = useGetDashboardUpdateDateQuery(
    {
      params: {
        clientId,
        ...(isTDAdminTCD && tenantId && { tenantId }),
      },
    },
    commonOptions
  );

  const {
    data: opportunitiesData,
    isLoading,
    isFetching,
  } = useGetPrefOriginOpportunitiesQuery(
    { input },
    commonOptions
  );

  const opportunities = opportunitiesData?.data ?? [];
  const totalCount = opportunitiesData?.meta?.countDocs ?? 0;
  const kpis = opportunitiesData?.kpis;
  const manufacturers = opportunitiesData?.manufacturers ?? [];

  const isKpiLoading = isLoading || areClientsLoading;

  // Handlers
  const handleRowClick = (row: PrefOriginOpportunity, manufacturerId?: string) => {
    setSelectedOpportunity(row);
    setInitialMid(manufacturerId);
  };

  const handleBackFromDetail = () => {
    setSelectedOpportunity(null);
    setInitialMid(undefined);
  };

  // Guard: show select client banner. `shouldRequireClientSelection` is
  // already false for the TD-admin-on-TCD-tenant case, so no extra
  // `isTDAdminTCD` carve-out is needed.
  if (shouldRequireClientSelection) {
    return <SelectClientBanner />;
  }

  // Detail view
  if (selectedOpportunity) {
    return (
      <Stack sx={{ p: { xs: 1.5, md: 2 } }}>
        <EntriesDetail
          opportunity={selectedOpportunity}
          onBack={handleBackFromDetail}
          dateFrom={dateFrom}
          dateTo={dateTo}
          initialManufacturerId={initialMid}
          manufacturers={manufacturers}
        />
      </Stack>
    );
  }

  return (
    <Stack sx={{ p: { xs: 1.5, md: 2 } }}>
      {/* Header */}
      <Grid container alignItems="center" sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <Typography
            variant="h4"
            fontWeight={tokens.fontWeight.bold}
            sx={{ fontSize: responsiveFontSizes.lg, lineHeight: 1.2 }}
          >
            FTA Savings Portal
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: tokens.text.secondary,
              fontSize: responsiveFontSizes.xs,
            }}
          >
            Identify and capture preferential duty savings across your supply chain
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: tokens.text.muted,
              fontSize: responsiveFontSizes.xs,
              mt: 0.25,
            }}
          >
            Last Updated: {formatDateOrTime(lastUpdated ?? new Date(), "both")}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{
              justifyContent: { xs: "center", sm: "flex-end" },
              mt: { xs: 2, sm: 0 },
            }}
          >
            <DateRangeFilter value={dateFilter} onSelectDate={setDateFilter} />
          </Stack>
        </Grid>
      </Grid>

      {/* KPI Cards */}
      <KpiCards
        totalPotentialSavings={kpis?.totalPotentialSavings ?? 0}
        opportunityCount={kpis?.opportunityCount ?? 0}
        fullyClaimedCount={kpis?.fullyClaimedCount ?? 0}
        programCount={kpis?.programCount ?? 0}
        isLoading={isKpiLoading}
      />

      {/* View toggle + toolbar */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mt: 3, mb: 2 }}
        flexWrap="wrap"
      >
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, val) => {
            if (!val) return;
            setViewMode(val);
            setPage(1);
          }}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: tokens.fontSize.sm2,
              fontWeight: tokens.fontWeight.semibold,
              px: 2,
              py: 0.5,
              borderColor: tokens.border.default,
              color: tokens.text.secondary,
              "&.Mui-selected": {
                backgroundColor: tokens.brand.primary,
                color: "#fff",
                borderColor: tokens.brand.primary,
                "&:hover": {
                  backgroundColor: tokens.brand.primary,
                },
              },
            },
          }}
        >
          <ToggleButton value="all">All Opportunities</ToggleButton>
          <ToggleButton value="by-manufacturer">By Manufacturer</ToggleButton>
        </ToggleButtonGroup>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 2 }}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ ml: { sm: "auto !important" }, width: { xs: "100%", sm: "auto" } }}
          flexWrap="wrap"
        >
          <TextField
            size="small"
            placeholder="Search HTS, country, program..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ minWidth: { xs: 0, sm: 260 }, width: { xs: "100%", sm: "auto" } }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="NOT_CLAIMED">Not Claimed</MenuItem>
              <MenuItem value="PARTIALLY_CLAIMED">Partially Claimed</MenuItem>
              <MenuItem value="FULLY_CLAIMED">Fully Claimed</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {/* Content */}
      {viewMode === "by-manufacturer" ? (
        <GroupedByManufacturer
          manufacturers={manufacturers}
          opportunities={opportunities}
          isLoading={isLoading || isFetching}
          onOpportunityClick={handleRowClick}
        />
      ) : (
        <OpportunitiesTable
          data={opportunities}
          isLoading={isLoading || isFetching}
          page={page}
          pageSize={pageSize}
          total={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          onRowClick={handleRowClick}
        />
      )}
    </Stack>
  );
};

export default PreferentialOrigin;
